import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { hasPermission } from '@/composables/usePermission'

export default function useCompanyList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications();
  const { getItemsAggregate, updateItem } = useLambda()
  const userData = store.state?.userStore?.userData
  const companies = ref([])
  const collection = 'company'

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
      { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
    ]
  })

  const perPage = ref(10)
  const totalCompanies = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)
  const companyFilter = computed(() => store.state.filtersStore.companyFilter)

  const fetchCompanies = async () => {
    isLoading.value = true

    try {
      const filter = {
        client_id: oid(userData.client.$oid),
        deleted: { $ne: true }
      }
      
      if (searchQuery.value) filter.name = { $regex: searchQuery.value, $options: 'i' }

      if (companyFilter.value.length > 0) {
        filter._id = { $in: companyFilter.value.map(e => oid(e)) }
      }
    
      const pipeline = [
        { $match: filter },
        { $project: { name: 1 } },
        { $facet: {
            count: [ { $count: 'totalItems' } ],
            pagination: [
              { $sort: { [sortBy.value]: isSortDirDesc.value ? -1 : 1, _id: isSortDirDesc.value ? -1 : 1 } },
              { $skip: (currentPage.value * perPage.value) - perPage.value || 0 },
              { $limit: perPage.value || 10 }
            ]
          }
        }
      ]
    
      const items = await getItemsAggregate({ collection, pipeline })

      companies.value = items[0]?.pagination || []
      totalCompanies.value = items[0]?.count[0]?.totalItems || 0
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.company_list_fetch_error'))
    } finally {
      isLoading.value = false
    }
  }

  const deleteCompany = async (id) => {
    if (!hasPermission('organization-companies', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const query = { _id: oid(id), client_id: oid(userData.client.$oid) }
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      showSuccessMessage(i18n.t('message.company_remove'))
      fetchCompanies()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (companies.value.length ? 1 : 0)
    const to = companies.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalCompanies.value })
  })

  watch([currentPage, perPage], () => {
    fetchCompanies()
  })

  watch([searchQuery, sortBy, isSortDirDesc, companyFilter], () => {
    currentPage.value = 1
    fetchCompanies()
  })

  return {
    fetchCompanies,
    tableColumns,
    perPage,
    currentPage,
    totalCompanies,
    showingMessage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    companies,
    deleteCompany,
    isLoading,
  }
}